import * as React from 'react';
import { Fragment } from 'react';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport, lastAssistantMessageIsCompleteWithToolCalls } from 'ai';
import type { UIMessage } from 'ai';
import { Button } from '@/components/forms/ui/button';
import { Input } from '@/components/forms/ui/input';
import { ToggleSwitch } from '@/components/forms/ui/toggle-switch';
import {
  AgentWorkingDisclosure,
  UserBubble,
  ThinkingIndicator,
  WorkspaceFooterWarning,
  WorkspaceThinkingBanner
} from './ai-chat-primitives';
import { CommitInFlightContext } from './shared/commit-card-frame';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons';

const SHOW_WORKING_KEY = 'bravo.chat.showWorking';

export type ChatHelpers = {
  sendMessage: (text: string) => void;
  isBusy: boolean;
  selectedSuggestions: Map<string, string>;
  onSuggestionSelect: (value: string, id: string) => void;
};

/** Resolve a confirmed-write tool call. Always called on every terminal outcome
 * (success / failed / cancelled) so `sendAutomaticallyWhen` resumes the agent. Mirrors the
 * SDK's discriminated union: success carries `output` (no errorText); failure carries
 * `errorText` (no output). */
export type ResolveToolOutput = (
  args:
    | { tool: string; toolCallId: string; state?: 'output-available'; output: unknown }
    | { tool: string; toolCallId: string; state: 'output-error'; errorText: string }
) => Promise<void>;

export type WorkspaceHelpers = {
  sendMessage: (text: string) => void;
  appendSystemNote: (text: string) => void;
  addToolOutput: ResolveToolOutput;
  isBusy: boolean;
  onClose: () => void;
};

type AiChatModalProps<TMessage extends UIMessage> = {
  open: boolean;
  onClose: () => void;
  title: string;
  steps?: string[];
  apiEndpoint: string;
  body?: Record<string, unknown>;
  /** Extra request-body fields resolved at send time (e.g. live workspace state) — unlike `body`,
   * which is captured once per transport, this is called on every send. */
  getExtraBody?: () => Record<string, unknown>;
  initialMessage: string;
  renderChatContent: (
    message: TMessage,
    messageIndex: number,
    helpers: ChatHelpers
  ) => React.ReactNode;
  renderWorkspaceContent: (allMessages: TMessage[], helpers: WorkspaceHelpers) => React.ReactNode;
  // Soft lock for commit cards: when a listed tool's calls are pending (input-available) on the
  // last assistant message, any message send first resolves them as cancelled (via
  // cancelledOutputFor) so the agent never receives a dangling unresolved tool call and can
  // re-propose. Calls whose Confirm was already clicked (CommitInFlightContext) are never
  // cancelled — the card resolves them itself when the write completes.
  commitTools?: string[];
  cancelledOutputFor?: (tool: string) => unknown;
};

export function AiChatModal<TMessage extends UIMessage>({
  open,
  onClose,
  title,
  steps,
  apiEndpoint,
  body,
  getExtraBody,
  initialMessage,
  renderChatContent,
  renderWorkspaceContent,
  commitTools,
  cancelledOutputFor
}: AiChatModalProps<TMessage>) {
  const chatScrollRef = React.useRef<HTMLDivElement | null>(null);
  const workspaceRef = React.useRef<HTMLDivElement | null>(null);
  // Sticky-bottom autoscroll: each pane follows new content only while it is already scrolled to
  // the bottom. Scrolling up pauses following (streaming updates must not yank the view away from
  // what the user is reading); scrolling back to the bottom re-arms it. Refs, not state — scroll
  // position changes must not cause re-renders.
  const chatStickToBottom = React.useRef(true);
  const workspaceStickToBottom = React.useRef(true);
  const hasSentInitial = React.useRef(false);
  const [currentStep, setCurrentStep] = React.useState(1);
  const [selectedSuggestions, setSelectedSuggestions] = React.useState<Map<string, string>>(
    new Map()
  );
  // Optional, persisted "show working" view: surfaces each assistant turn's tool calls inline for
  // transparency / debugging. Off by default; the tool data is already in the message stream.
  const [showWorking, setShowWorking] = React.useState<boolean>(() => {
    try {
      return localStorage.getItem(SHOW_WORKING_KEY) === 'true';
    } catch {
      return false;
    }
  });
  React.useEffect(() => {
    try {
      localStorage.setItem(SHOW_WORKING_KEY, String(showWorking));
    } catch {
      // ignore (e.g. storage disabled in private mode)
    }
  }, [showWorking]);

  const transport = React.useMemo(
    () =>
      new DefaultChatTransport<TMessage>({
        api: apiEndpoint,
        body,
        prepareSendMessagesRequest: ({ id, messages, trigger, messageId }) => ({
          body: {
            ...body,
            ...(getExtraBody?.() ?? {}),
            id,
            trigger,
            messageId,
            messages: stripZohoHtmlFromMessages(messages)
          }
        })
      }),
    [apiEndpoint, body, getExtraBody]
  );

  const { messages, status, sendMessage, setMessages, addToolOutput } = useChat<TMessage>({
    transport,
    // When a confirmed-write tool is resolved via addToolOutput, automatically resume the
    // agent so it sees the result and continues the turn.
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls
  });

  const [input, setInput] = React.useState('');
  const isBusy = status === 'submitted' || status === 'streaming';

  const resolveToolOutput = React.useCallback<ResolveToolOutput>(
    async args => {
      // useChat's addToolOutput is typed against this message's tools; the modal is generic,
      // so we widen here and let the typed callers (workspace cards) build the correctly
      // discriminated payload (output for success, errorText for failure).
      await (addToolOutput as unknown as (a: typeof args) => Promise<void>)(args);
    },
    [addToolOutput]
  );

  // Read at send time (after awaits) — the values captured at render time can be stale by then.
  const statusRef = React.useRef(status);
  statusRef.current = status;
  const messagesRef = React.useRef(messages);
  messagesRef.current = messages;

  // Commit calls whose Confirm has been clicked (via CommitInFlightContext). Entries are permanent:
  // a confirmed call is always resolved by its card (success, failure, or cancel), and the SDK's
  // addToolOutput would silently OVERWRITE an already-resolved part, so "never cancel a confirmed
  // call again" is exactly the invariant we want — no removal, no re-render timing window.
  const inFlightCommitsRef = React.useRef<Set<string>>(new Set());
  const commitInFlightRegistry = React.useMemo(
    () => ({ markInFlight: (toolCallId: string) => inFlightCommitsRef.current.add(toolCallId) }),
    []
  );

  // Soft lock: resolve ALL pending (unconfirmed) commit-tool calls on the last real assistant
  // message as cancelled. Cancelling only some would strand the queue — the automatic resume fires
  // only once EVERY tool part is resolved. Confirmed calls are skipped: the card resolves them when
  // the write completes. Returns true when the caller must DEFER its send — either a cancel was
  // issued or a confirmed write is still executing; both end in a resolution that triggers the
  // automatic resume turn, and the deferred message follows on the next idle transition.
  const cancelPendingCommits = React.useCallback(async (): Promise<boolean> => {
    if (!commitTools || !cancelledOutputFor) return false;
    // Skip synthetic system notes (assistant-role messages with only data parts): the commit
    // proposal lives on the last assistant message with real (text / tool) parts.
    const last = messagesRef.current.findLast(
      m => m.role === 'assistant' && m.parts.some(p => !String(p.type).startsWith('data-'))
    );
    if (!last) return false;
    let defer = false;
    for (const part of last.parts) {
      if (typeof part.type !== 'string' || !part.type.startsWith('tool-')) continue;
      const tool = part.type.slice('tool-'.length);
      if (!commitTools.includes(tool)) continue;
      const tp = part as unknown as { toolCallId: string; state: string };
      if (tp.state !== 'input-available') continue;
      defer = true;
      if (inFlightCommitsRef.current.has(tp.toolCallId)) continue;
      await resolveToolOutput({
        tool,
        toolCallId: tp.toolCallId,
        state: 'output-available',
        output: cancelledOutputFor(tool)
      });
    }
    return defer;
  }, [commitTools, cancelledOutputFor, resolveToolOutput]);

  // The single pre-send gate BOTH send paths go through: cancel/skip pending commits, then re-check
  // busyness AFTER the await (render-time values can be stale by then). 'deferred' means the caller
  // must hold the message for the next idle transition.
  const guardedSend = React.useCallback(
    async (text: string): Promise<'sent' | 'deferred'> => {
      const mustDefer = await cancelPendingCommits();
      if (mustDefer || statusRef.current === 'submitted' || statusRef.current === 'streaming') {
        return 'deferred';
      }
      sendMessage({ text });
      return 'sent';
    },
    [cancelPendingCommits, sendMessage]
  );

  // A send made while the agent is mid-turn (a typed message OR a workspace pick) is QUEUED and
  // flushed in order when the turn completes — so the chat input and the landing options are usable
  // the moment the dialog opens instead of being greyed out until the agent's greeting finishes.
  // FIFO so several queued messages drain one per turn. `queuedCount` mirrors the queue for display.
  const pendingSendsRef = React.useRef<string[]>([]);
  const [queuedCount, setQueuedCount] = React.useState(0);
  const queueSendMessage = React.useCallback(
    (text: string) => {
      void (async () => {
        if ((await guardedSend(text)) === 'deferred') {
          pendingSendsRef.current.push(text);
          setQueuedCount(pendingSendsRef.current.length);
        }
      })();
    },
    [guardedSend]
  );
  // Drain ONE queued message per idle transition. The effect depends on isBusy (not the queue), so
  // it fires once each time the agent goes idle: it sends the next message, which flips isBusy true
  // again, and the next one goes out when that turn finishes. The turn that just ended may have
  // PROPOSED a commit card (queued messages were typed before it existed), so the drain goes
  // through the same guardedSend gate: on 'deferred' the message stays AT THE HEAD of the queue
  // (peek, not shift — preserving order) and the resolution that caused the deferral re-fires this
  // effect via the next isBusy round-trip.
  const drainingRef = React.useRef(false);
  React.useEffect(() => {
    if (isBusy || drainingRef.current || pendingSendsRef.current.length === 0) return;
    drainingRef.current = true;
    void (async () => {
      try {
        if ((await guardedSend(pendingSendsRef.current[0])) === 'sent') {
          pendingSendsRef.current.shift();
          setQueuedCount(pendingSendsRef.current.length);
        }
      } finally {
        drainingRef.current = false;
      }
    })();
  }, [isBusy, guardedSend]);

  // Scan messages for step-update data parts
  React.useEffect(() => {
    for (const m of messages) {
      if (m.role !== 'assistant') continue;
      for (const part of m.parts) {
        if (part.type === 'data-step-update') {
          const data = part.data as { step: number };
          setCurrentStep(data.step);
        }
      }
    }
  }, [messages]);

  React.useEffect(() => {
    if (open && !hasSentInitial.current) {
      hasSentInitial.current = true;
      sendMessage({ text: initialMessage });
    }
  }, [open, initialMessage, sendMessage]);

  // Instant (not smooth) scrolls: a smooth animation emits intermediate scroll events that read as
  // "not at the bottom" and would falsely un-stick the panes mid-stream.
  React.useEffect(() => {
    const el = chatScrollRef.current;
    if (el && chatStickToBottom.current) {
      el.scrollTo({ top: el.scrollHeight });
    }
  }, [messages.length, status]);

  React.useEffect(() => {
    const el = workspaceRef.current;
    if (el && workspaceStickToBottom.current) {
      el.scrollTo({ top: el.scrollHeight });
    }
  }, [messages]);

  function handleSuggestionSelect(value: string, id: string) {
    setSelectedSuggestions(prev => new Map(prev).set(id, value));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!input.trim()) return;
    // Queue-aware: if the agent is mid-turn (e.g. still greeting on open), this is sent when it's
    // ready instead of being dropped — the input is never disabled.
    queueSendMessage(input.trim());
    setInput('');
  }

  const chatHelpers: ChatHelpers = {
    sendMessage: queueSendMessage,
    isBusy,
    selectedSuggestions,
    onSuggestionSelect: handleSuggestionSelect
  };

  const appendSystemNote = React.useCallback(
    (text: string) => {
      setMessages(prev => [
        ...prev,
        {
          id: `system-note-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          role: 'assistant',
          parts: [{ type: 'data-system-note', data: { text } }]
        } as unknown as TMessage
      ]);
    },
    [setMessages]
  );

  const workspaceHelpers: WorkspaceHelpers = {
    // Queue-aware: a pick made while the agent is mid-turn is sent once the turn completes.
    sendMessage: queueSendMessage,
    appendSystemNote,
    addToolOutput: resolveToolOutput,
    isBusy,
    onClose
  };

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-15"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-15">
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </TransitionChild>
        <div className="fixed inset-0 z-10 flex items-center justify-center">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-15 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95">
            <DialogPanel className="flex h-[80vh] w-[80vw] flex-col rounded-2xl bg-white shadow-xl">
              <div className="border-b bg-gray-50/80">
                <div className="flex items-start justify-between px-6 pt-4 pb-3">
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">{title}</h2>
                  </div>
                  <div className="flex items-center gap-3">
                    <ToggleSwitch
                      checked={showWorking}
                      onChange={setShowWorking}
                      label="Show working"
                      labelPosition="left"
                      size="sm"
                    />
                    <button
                      onClick={onClose}
                      className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600">
                      <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                {steps ? (
                  <div className="px-6 pb-3">
                    <StepIndicator steps={steps} currentStep={currentStep} />
                  </div>
                ) : null}
              </div>

              <div className="flex min-h-0 flex-1">
                {/* Chat Panel */}
                <div className="flex w-2/5 flex-col border-r border-gray-200">
                  <div
                    ref={chatScrollRef}
                    onScroll={() => {
                      const el = chatScrollRef.current;
                      if (el) chatStickToBottom.current = isScrolledToBottom(el);
                    }}
                    className="flex-1 overflow-y-auto px-4 py-4">
                    <div className="space-y-4">
                      {messages.map((m, mi) => {
                        if (m.role === 'user') {
                          if (mi === 0) return null;
                          return (
                            <div key={m.id}>
                              <UserBubble parts={m.parts} />
                            </div>
                          );
                        }
                        return (
                          <React.Fragment key={m.id}>
                            {renderChatContent(m as TMessage, mi, chatHelpers)}
                            {showWorking ? <AgentWorkingDisclosure parts={m.parts} /> : null}
                          </React.Fragment>
                        );
                      })}

                      {isBusy ? <ThinkingIndicator /> : null}
                    </div>
                  </div>

                  <form onSubmit={handleSubmit} className="border-t px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Input
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        placeholder={
                          isBusy ? 'Type a message (sends when ready)...' : 'Type a message...'
                        }
                        className="h-10 rounded-xl px-3 text-sm"
                      />
                      <Button
                        type="submit"
                        disabled={!input.trim()}
                        className="h-10 rounded-xl text-sm">
                        Send
                      </Button>
                    </div>
                    {queuedCount > 0 ? (
                      <div className="mt-1 text-[11px] text-gray-400">
                        {queuedCount} message{queuedCount > 1 ? 's' : ''} queued — sending when the
                        assistant is ready.
                      </div>
                    ) : null}
                  </form>
                </div>

                {/* Workspace Panel */}
                <div className="flex w-3/5 flex-col bg-gray-50/50">
                  {isBusy ? <WorkspaceThinkingBanner /> : null}
                  <div
                    ref={workspaceRef}
                    onScroll={() => {
                      const el = workspaceRef.current;
                      if (el) workspaceStickToBottom.current = isScrolledToBottom(el);
                    }}
                    className="flex-1 overflow-y-auto p-6">
                    {/* Commit cards register their Confirm clicks here so the soft-cancel above
                        never false-cancels a write that is already executing. */}
                    <CommitInFlightContext.Provider value={commitInFlightRegistry}>
                      {renderWorkspaceContent(messages as TMessage[], workspaceHelpers)}
                    </CommitInFlightContext.Provider>
                  </div>
                  <WorkspaceFooterWarning />
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}

// Within ~40px of the bottom counts as "at the bottom", so sub-pixel rounding and small layout
// shifts don't break the autoscroll follow.
function isScrolledToBottom(el: HTMLElement): boolean {
  return el.scrollHeight - el.scrollTop - el.clientHeight < 40;
}

function stripZohoHtmlFromMessages<TMessage extends UIMessage>(messages: TMessage[]): TMessage[] {
  return messages.map(message => {
    if (!message.parts.some(hasZohoNotes)) return message;
    return {
      ...message,
      parts: message.parts.map(part => {
        if (!hasZohoNotes(part)) return part;
        const data = part.data as { zoho_notes_module: Array<Record<string, unknown>> };
        return {
          ...part,
          data: {
            ...data,
            zoho_notes_module: data.zoho_notes_module.map(
              ({ content_html: _omit, ...note }) => note
            )
          }
        };
      })
    };
  });
}

function hasZohoNotes(part: unknown): part is { data: { zoho_notes_module: unknown[] } } {
  if (typeof part !== 'object' || part === null || !('data' in part)) return false;
  const data = (part as { data?: unknown }).data;
  return (
    typeof data === 'object' &&
    data !== null &&
    Array.isArray((data as { zoho_notes_module?: unknown }).zoho_notes_module)
  );
}

function StepIndicator({ steps, currentStep }: { steps: string[]; currentStep: number }) {
  return (
    <div className="flex items-center">
      {steps.map((label, i) => {
        const stepNum = i + 1;
        const isActive = stepNum === currentStep;
        const isPast = stepNum < currentStep;
        return (
          <React.Fragment key={i}>
            {i > 0 ? (
              <div
                className={`mx-2 h-px w-10 ${isPast || isActive ? 'bg-primary/40' : 'bg-gray-200'}`}
              />
            ) : null}
            <div className="flex items-center gap-2">
              <div
                className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${
                  isActive
                    ? 'bg-primary text-white shadow-sm'
                    : isPast
                      ? 'bg-primary/15 text-primary'
                      : 'bg-gray-200 text-gray-400'
                }`}>
                {isPast ? '\u2713' : stepNum}
              </div>
              <span
                className={`text-sm ${
                  isActive
                    ? 'font-semibold text-gray-900'
                    : isPast
                      ? 'font-medium text-gray-500'
                      : 'text-gray-400'
                }`}>
                {label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}
